import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  Button,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Description as DocumentIcon,
  HourglassTop as AwaitingApprovalIcon,
  CheckCircle as ApprovedIcon,
  Groups as OnboardedClientsIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import moment from 'moment';
import { DateInputField } from 'components';
import {
  getOnboardRequestDetailsForAnalyticReport,
  getClientOnboardingStates,
  getElecodes,
  getASMBasedStateId,
  getBUCodes,
  getARUsers,
  downloadAnalyticsReport
} from 'services';
import { DashboardBarChartCard } from 'modules/quote/Dashboard/components/DashboardBarChartCard';
import { DashboardPieChartCard } from 'modules/quote/Dashboard/components/DashboardPieChartCard';

interface OnboardingClient {
  buCode: string;
  state: string;
  approvedOn: string;
  requestStatus: string;
  approverName?: string;
  asmName?: string;
  eleCode?: string;
}

const stateColorMap: Record<string, string> = {
  Victoria: '#003883',
  'Western Australia': '#5765a6',
  Queensland: '#ee7d11',
  'New South Wales': '#41a85f',
  Tasmania: '#8e44ad',
};

interface StatCardProps {
  title: string;
  count: number;
  color: string;
}

const getStatCardIcon = (title: string) => {
  const normalizedTitle = String(title || '').trim().toLowerCase();

  if (normalizedTitle === 'awaiting approval') {
    return <AwaitingApprovalIcon sx={{ fontSize: 30 }} />;
  }

  if (normalizedTitle === 'approved') {
    return <ApprovedIcon sx={{ fontSize: 30 }} />;
  }

  if (normalizedTitle === 'total onboarded clients') {
    return <OnboardedClientsIcon sx={{ fontSize: 30 }} />;
  }

  return <DocumentIcon sx={{ fontSize: 30 }} />;
};

const StatCard: React.FC<StatCardProps> = ({ title, count, color }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
            {count.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: color,
            width: 56,
            height: 56,
            boxShadow: '0 4px 14px 0 rgba(0,0,0,0.15)'
          }}
        >
          {getStatCardIcon(title)}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

export function OnboardingClientPage() {
  const today = moment().startOf('day').format('YYYY-MM-DD');
  const defaultFromDate = moment().startOf('day').subtract(6, 'days').format('YYYY-MM-DD');
  const initialFormData = {
    fromDate: defaultFromDate,
    toDate: today,
    stateId: '',
    asm: '',
    buCode: '',
    approver: '',
    ele: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [australianStates, setAustralianStates] = useState<any[]>([]);
  const [asmOptions, setAsmOptions] = useState<any[]>([]);
  const [eleCodes, setEleCodes] = useState<any[]>([]);
  const [buCodes, setBuCodes] = useState<any[]>([]);
  const [arUsers, setARUsers] = useState<any[]>([]);

  const [onboardingClients, setOnboardingClients] = useState<OnboardingClient[]>([]);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const jwt: any = localStorage.getItem('token');
  const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

  const normalizeClient = (client: any): OnboardingClient => ({
    buCode: String(client.buCode ?? client.bu ?? client.proposedBUCode ?? '').trim(),
    state: String(client.state ?? '').trim(),
    approvedOn: String(client.approvedOn ?? client.onboardedDate ?? '').trim(),
    requestStatus: String(client.requestStatus ?? client.status ?? '').trim(),
    approverName: String(client.approverName ?? client.approver ?? client.approverId ?? '').trim(),
    asmName: String(client.asmName ?? client.asm ?? client.asmId ?? '').trim(),
    eleCode: String(client.eleCode ?? client.elecode ?? client.ele ?? '').trim(),
  });

  const extractDownloadFileName = (contentDisposition?: string) => {
    if (!contentDisposition) {
      return 'OnBoardRequests.xlsx';
    }

    const utf8NameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8NameMatch?.[1]) {
      return decodeURIComponent(utf8NameMatch[1].replace(/\"/g, '').trim());
    }

    const plainNameMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (plainNameMatch?.[1]) {
      return plainNameMatch[1].replace(/\"/g, '').trim();
    }

    return 'OnBoardRequests.xlsx';
  };

  const triggerFileDownload = (response: any) => {
    if (!response?.data) {
      return;
    }

    const contentType = response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const contentDisposition = response?.headers?.['content-disposition'];
    const fileName = extractDownloadFileName(contentDisposition);

    const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fetchOnboardClientData = async (filters: typeof formData, type: 'download' | 'view' = 'view') => {
    if (type === 'download') {
      const effectiveFromDate = filters.fromDate || defaultFromDate;
      const effectiveToDate = filters.toDate || today;
      const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
      const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
      const selectedState = filters.stateId !== '' && filters.stateId !== null && filters.stateId !== undefined ? filters.stateId : null;
      const selectedAsm = filters.asm !== '' ? filters.asm : null;
      const selectedBu = filters.buCode !== '' ? filters.buCode : null;
      const selectedApprover = filters.approver !== '' ? filters.approver : null;
      const selectedEle = filters.ele !== '' ? filters.ele : null;

      const response = await downloadAnalyticsReport(
        apiFromDate,
        apiToDate,
        selectedState,
        selectedAsm,
        selectedApprover,
        selectedBu,
        selectedEle
      );

      triggerFileDownload(response);
      return;
    }

    setIsLoading(true);

    const effectiveFromDate = filters.fromDate || defaultFromDate;
    const effectiveToDate = filters.toDate || today;
    const apiFromDate = moment(effectiveFromDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const apiToDate = moment(effectiveToDate, 'YYYY-MM-DD', true).format('YYYY-MM-DD');
    const selectedState = filters.stateId !== '' && filters.stateId !== null && filters.stateId !== undefined ? filters.stateId : null;
    const selectedAsm = filters.asm !== '' ? filters.asm : null;
    const selectedBu = filters.buCode !== '' ? filters.buCode : null;
    const selectedApprover = filters.approver !== '' ? filters.approver : null;
    const selectedEle = filters.ele !== '' ? filters.ele : null;

    try {
      const response = await getOnboardRequestDetailsForAnalyticReport(
        apiFromDate,
        apiToDate,
        selectedState,
        selectedAsm,
        selectedApprover,
        selectedBu,
        selectedEle
      );

      if (response && response.data) {
        setOnboardingClients(Array.isArray(response.data) ? response.data.map(normalizeClient) : []);
      } else {
        setOnboardingClients([]);
      }
      setHasFetchedData(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOnboardClientData(formData, 'view');
  }, []);

  useEffect(() => {
    getClientOnboardingStates().then((res: any) => {
      if (res?.status === 200) {
        setAustralianStates(res?.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (formData?.stateId !== '' && formData?.stateId !== null && formData?.stateId !== undefined) {
      getASMBasedStateId(formData?.stateId).then((res: any) => {
        if (res?.status === 200) {
          setAsmOptions(res?.data || []);
        } else {
          setAsmOptions([]);
        }
      }).catch(() => setAsmOptions([]));
      return;
    }

    setAsmOptions([]);
  }, [formData?.stateId]);

  useEffect(() => {
    if (labUserCheck?.UserType === 'Internal') {
      getElecodes().then((res: any) => {
        setEleCodes(res?.data || []);
      });
    }
  }, []);

  useEffect(() => {
    getARUsers().then((res: any) => {
      if (res?.status === 200) {
        setARUsers(res?.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (formData?.ele) {
      getBUCodes(formData?.ele).then((res: any) => {
        setBuCodes(res?.data || []);
      }).catch(() => setBuCodes([]));
      return;
    }

    setBuCodes([]);
  }, [formData?.ele]);

  const handleApproverFilterChange = (event: SelectChangeEvent) => {
    setFormData((prev) => ({ ...prev, approver: event.target.value }));
  };

  const handleSearch = () => {
    fetchOnboardClientData(formData, 'view');
  };

  const handleClearAll = () => {
    setFormData(initialFormData);
    fetchOnboardClientData(initialFormData, 'view');
  };

  const hasDefaultDates = formData.fromDate === defaultFromDate && formData.toDate === today;
  const hasFilterChanges = Object.keys(formData).some((key) => {
    const typedKey = key as keyof typeof formData;
    return formData[typedKey] !== initialFormData[typedKey];
  });
  const isSearchEnabled = hasDefaultDates || hasFilterChanges;

  const getApproverValue = (client: OnboardingClient) => String(
    client.approverName
    ?? ''
  ).trim();

  const filteredOnboardingData = onboardingClients;

  const buBarData = filteredOnboardingData.reduce<Record<string, number>>((acc, client) => {
    const buCode = String(client.buCode || '').trim();
    if (!buCode) {
      return acc;
    }
    acc[buCode] = (acc[buCode] || 0) + 1;
    return acc;
  }, {});

  const buChartData = Object.entries(buBarData).map(([buCode, count]) => ({ buCode, count }));

  const approverBarData = filteredOnboardingData.reduce<Record<string, number>>((acc, client) => {
    const approverName = getApproverValue(client);
    if (!approverName) {
      return acc;
    }
    acc[approverName] = (acc[approverName] || 0) + 1;
    return acc;
  }, {});

  const approverChartData = Object.entries(approverBarData).map(([approverName, count]) => ({ approverName, count }));

  const stateBarData = filteredOnboardingData.reduce<Record<string, number>>((acc, client) => {
    const stateName = String(client.state || '').trim();
    if (!stateName) {
      return acc;
    }
    acc[stateName] = (acc[stateName] || 0) + 1;
    return acc;
  }, {});

  const stateChartData = Object.entries(stateBarData).map(([state, count]) => ({ state, count }));

  const normalizeRequestStatus = (status: string) => {
    const normalized = String(status || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalized) {
      return 'Unassigned';
    }

    const statusMap: Record<string, string> = {
      'awaiting approval': 'Awaiting Approval',
      'awaiting approvel': 'Awaiting Approval',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    };

    return statusMap[normalized] || String(status).trim();
  };

  const requestStatusTotals = filteredOnboardingData.reduce<Record<string, number>>((acc, client) => {
    const requestStatus = normalizeRequestStatus(client.requestStatus);
    acc[requestStatus] = (acc[requestStatus] || 0) + 1;
    return acc;
  }, {});

  const requestStatusPalette = ['#003883', '#ee7d11', '#5765a6', '#41a85f', '#8e44ad', '#c0392b'];
  const requestStatusPieData = Object.entries(requestStatusTotals)
    .map(([name, value], index) => ({ name, value, color: requestStatusPalette[index % requestStatusPalette.length] }))
    .filter((item) => item.value > 0);

  const renderNoDataFallback = (title: string) => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6">
          No data found.
        </Typography>
      </Box>
    </Box>
  );

  const renderLoadingFallback = () => (
    <Box
      sx={{
        p: 3,
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        minHeight: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={32} />
        <Typography variant="h6">
          Loading dashboard data...
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Grid
        container
        component="form"
        spacing={2}
        sx={{ mb: 4, backgroundColor: '#f5f5f5', p: 2, borderRadius: '6px', width: '100%', ml: 0, mt: 0 }}
        onSubmit={(event) => {
          event.preventDefault();
          if (isSearchEnabled) handleSearch();
        }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="From Date"
            value={formData.fromDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, fromDate: value }))}
            max={formData.toDate || today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DateInputField
            fullWidth
            label="To Date"
            value={formData.toDate}
            onChange={(value) => setFormData((prev) => ({ ...prev, toDate: value }))}
            min={formData.fromDate}
            max={today}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '12px' }}>Approver</InputLabel>
            <Select value={formData.approver} label="Approver" onChange={handleApproverFilterChange}>
              {arUsers.map((approverName) => (
                <MenuItem key={approverName.id} value={approverName.id} sx={{ fontSize: '12px' }}>
                  {approverName.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '12px' }}>Report State</InputLabel>
            <Select value={formData?.stateId ?? ''} label="Report State" onChange={(e) => setFormData({ ...formData, stateId: e.target.value })} sx={{ fontSize: '12px' }}>
              {australianStates.map((option) => (<MenuItem key={option.id} value={option.id} sx={{ fontSize: '12px' }}>{option.name}</MenuItem>))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="asm-label" sx={{ fontSize: '12px' }}>
              ASM
            </InputLabel>
            <Select
              labelId="asm-label"
              value={formData.asm || ''}
              label="ASM"
              onChange={(e) => setFormData({ ...formData, asm: e.target.value })}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 240, overflowY: 'auto' },
                },
              }}
              sx={{ fontSize: '12px' }}
            >
              {asmOptions?.length > 0 ? (
                asmOptions.map((option) => (
                  <MenuItem
                    key={option.userID}
                    value={option.userID}
                    sx={{ fontSize: '12px' }}
                  >
                    {option.asmName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ fontSize: '12px' }}>
                  No data available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="ele-label" sx={{ fontSize: '12px' }}>ELE</InputLabel>
            <Select labelId="ele-label" value={formData?.ele} label="ELE" onChange={(e) => setFormData({ ...formData, ele: e.target.value })} MenuProps={{ PaperProps: { style: { maxHeight: 240, overflowY: 'auto' } } }} sx={{ fontSize: '12px' }}>
              {eleCodes?.map((option) => (<MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>{option}</MenuItem>))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={buCodes || []}
            getOptionLabel={(option) => option}
            value={formData?.buCode || null}
            onChange={(e, value) => setFormData({ ...formData, buCode: value || '' })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="BU"
                size="small"
              />
            )}
            isOptionEqualToValue={(option, value) => option === value}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-start' }, gap: 1, width: '100%' }}>
            <Button type="submit" variant="contained" disabled={!isSearchEnabled}>Search</Button>
            <Button type="button" variant="outlined" onClick={handleClearAll}>Clear All</Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading ? (
        renderLoadingFallback()
      ) : hasFetchedData && filteredOnboardingData.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', border: '1px solid #ccc', borderRadius: '6px' }}>
          <Typography variant="h6">
            No data found.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Awaiting Approval"
                count={requestStatusTotals['Awaiting Approval'] || 0}
                color="#003883"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Approved"
                count={requestStatusTotals.Approved || 0}
                color="#5765a6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Onboarded Clients"
                count={filteredOnboardingData.length}
                color="#ee7d11"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" color="primary" onClick={() => fetchOnboardClientData(formData, 'download')} startIcon={<DownloadIcon />}>
              Download Report
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} mb={2}>
              {approverChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Onboard Requests by Approver"
                  data={approverChartData}
                  xDataKey="approverName"
                  barName="Onboard Requests by Approver"
                  barColor="#5765a6"
                  barCategoryGap="20%"
                  xAxisAngle={-20}
                  xAxisHeight={80}
                />
              ) : renderNoDataFallback('Onboard Requests by Approver')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {stateChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Onboard Requests by State"
                  data={stateChartData}
                  xDataKey="state"
                  barName="Onboard Requests by State"
                  barCategoryGap="25%"
                  xAxisAngle={-20}
                  xAxisHeight={70}
                  getCellFill={(entry) => stateColorMap[entry.state] || '#999'}
                />
              ) : renderNoDataFallback('Onboard Requests by State')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {buChartData.length > 0 ? (
                <DashboardBarChartCard
                  title="Onboard Requests by BU"
                  data={buChartData}
                  xDataKey="buCode"
                  barName="Onboard Requests by BU Code"
                  barColor="#003883"
                  barCategoryGap="15%"
                  xAxisAngle={-35}
                  xAxisHeight={100}
                />
              ) : renderNoDataFallback('Onboard Requests by BU')}
            </Grid>

            <Grid item xs={12} md={6} mb={2}>
              {requestStatusPieData.length > 0 ? (
                <DashboardPieChartCard
                  title="Request Status"
                  data={requestStatusPieData}
                  outerRadius={120}
                />
              ) : renderNoDataFallback('Request Status')}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
